import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Megaphone,
  Shield,
  Phone,
  CreditCard,
  Clock,
  Play,
  Pause,
  DollarSign,
  CheckCircle2,
  FileText,
  Users,
  Target,
  TrendingUp,
  Globe,
  Mail,
  MessageSquare,
  Video,
  Mic,
  Calendar,
  Building2,
  Scale,
  Loader2,
  ChevronRight,
  AlertCircle,
  Sparkles,
  Bot,
  Headphones
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

// Service pricing tiers
const SERVICE_PRICING = {
  advisory: {
    name: 'Advisory Call',
    perMinute: 2.99,
    description: 'Live expert business advice',
    icon: Headphones
  },
  marketing: {
    name: 'Marketing Consultation',
    perMinute: 3.99,
    description: 'Marketing strategy guidance',
    icon: Megaphone
  },
  legal: {
    name: 'Legal Guidance',
    perMinute: 4.99,
    description: 'Business legal consultation',
    icon: Scale
  },
  compliance: {
    name: 'Compliance Review',
    perMinute: 3.49,
    description: 'Regulatory compliance help',
    icon: Shield
  },
  formation: {
    name: 'Formation Assistance',
    perMinute: 2.49,
    description: 'Business entity setup help',
    icon: Building2
  }
};

// Industry options
const INDUSTRIES = [
  'Restaurant & Food Service',
  'Retail & E-commerce',
  'Professional Services',
  'Healthcare & Medical',
  'Construction & Trades',
  'Technology & SaaS',
  'Real Estate',
  'Manufacturing',
  'Transportation & Logistics',
  'Education & Training',
  'Beauty & Wellness',
  'Entertainment & Media',
  'Finance & Insurance',
  'Non-Profit',
  'Other'
];

// Budget tiers
const BUDGET_TIERS = [
  { id: 'startup', label: 'Startup', range: '$0 - $500/mo' },
  { id: 'growth', label: 'Growth', range: '$500 - $2,000/mo' },
  { id: 'scale', label: 'Scale', range: '$2,000 - $10,000/mo' },
  { id: 'enterprise', label: 'Enterprise', range: '$10,000+/mo' }
];

const AdvizzuServices = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  
  // Marketing Planner State
  const [marketingIndustry, setMarketingIndustry] = useState('');
  const [marketingBudget, setMarketingBudget] = useState('');
  const [marketingPlan, setMarketingPlan] = useState(null);
  const [isGeneratingMarketing, setIsGeneratingMarketing] = useState(false);
  
  // Compliance State
  const [complianceIndustry, setComplianceIndustry] = useState('');
  const [complianceData, setComplianceData] = useState(null);
  const [isGeneratingCompliance, setIsGeneratingCompliance] = useState(false);
  
  // Advisory Call State
  const [selectedService, setSelectedService] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callCost, setCallCost] = useState(0);
  const [scheduledCall, setScheduledCall] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Payment State
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [isPaymentSetup, setIsPaymentSetup] = useState(false);
  
  // Identity State
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientCompany, setClientCompany] = useState('');
  const [isIdentityVerified, setIsIdentityVerified] = useState(false);

  // Generate Marketing Plan
  const generateMarketingPlan = async () => {
    if (!marketingIndustry || !marketingBudget) return;
    
    setIsGeneratingMarketing(true);
    try {
      const budgetAmounts = {
        startup: 250,
        growth: 1250,
        scale: 6000,
        enterprise: 15000
      };
      
      const response = await axios.post(`${API}/marketing/generate-strategy`, {
        industry: marketingIndustry,
        monthly_budget: budgetAmounts[marketingBudget] || 1000,
        business_info: { hide_costs: true }
      });
      
      setMarketingPlan(response.data.strategy);
    } catch (error) {
      console.error('Error generating marketing plan:', error);
      // Set fallback plan
      setMarketingPlan(getMarketingFallback(marketingIndustry, marketingBudget));
    } finally {
      setIsGeneratingMarketing(false);
    }
  };

  // Generate Compliance Data
  const generateComplianceData = async () => {
    if (!complianceIndustry) return;
    
    setIsGeneratingCompliance(true);
    try {
      const response = await axios.post(`${API}/licensing/generate-requirements`, {
        business_type: 'LLC',
        industry: complianceIndustry,
        state: 'California',
        city: 'San Diego'
      });
      
      setComplianceData(response.data.requirements);
    } catch (error) {
      console.error('Error generating compliance data:', error);
      setComplianceData(getComplianceFallback(complianceIndustry));
    } finally {
      setIsGeneratingCompliance(false);
    }
  };

  // Setup Identity
  const setupIdentity = () => {
    if (clientName && clientEmail) {
      setIsIdentityVerified(true);
    }
  };

  // Setup Payment
  const setupPayment = () => {
    if (cardNumber && cardExpiry && cardCvc && cardName) {
      setIsPaymentSetup(true);
    }
  };

  // Start Advisory Call Timer
  const startCall = () => {
    if (!isPaymentSetup || !isIdentityVerified || !selectedService || !phoneNumber) {
      alert('Please complete identity verification, payment setup, select a service, and enter your phone number first.');
      return;
    }
    
    setIsCallActive(true);
    setCallDuration(0);
    setCallCost(0);
    
    // Simulate call - in production this would connect to Twilio
    const interval = setInterval(() => {
      setCallDuration(prev => {
        const newDuration = prev + 1;
        const rate = SERVICE_PRICING[selectedService]?.perMinute || 2.99;
        setCallCost((newDuration / 60) * rate);
        return newDuration;
      });
    }, 1000);
    
    // Store interval for cleanup
    window.callInterval = interval;
  };

  // End Advisory Call
  const endCall = async () => {
    setIsCallActive(false);
    if (window.callInterval) {
      clearInterval(window.callInterval);
    }
    
    // Log the call
    try {
      await axios.post(`${API}/advizzu/log-call`, {
        client_name: clientName,
        client_email: clientEmail,
        service_type: selectedService,
        duration_seconds: callDuration,
        total_cost: callCost,
        phone_number: phoneNumber
      });
    } catch (error) {
      console.error('Error logging call:', error);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Fallback data generators
  const getMarketingFallback = (industry, budget) => ({
    marketing_plan: {
      industry: industry,
      budget_tier: budget,
      primary_channels: [
        { channel: 'Digital Presence', tactics: ['Website optimization', 'Local SEO', 'Google Business Profile'] },
        { channel: 'Social Media', tactics: ['Platform selection', 'Content calendar', 'Community engagement'] },
        { channel: 'Content Marketing', tactics: ['Blog strategy', 'Video content', 'Email campaigns'] }
      ],
      content_strategy: {
        content_types: ['Educational articles', 'How-to guides', 'Industry insights', 'Customer stories'],
        frequency: 'Consistent posting schedule',
        topics: ['Industry trends', 'Problem-solving content', 'Behind-the-scenes']
      },
      monthly_action_plan: [
        { month: 1, focus: 'Foundation', tasks: ['Audit current presence', 'Set up tracking', 'Define target audience'] },
        { month: 2, focus: 'Launch', tasks: ['Start content production', 'Activate social channels', 'Begin outreach'] },
        { month: 3, focus: 'Optimize', tasks: ['Analyze results', 'Refine targeting', 'Scale what works'] }
      ],
      quick_wins: ['Claim all business listings', 'Optimize Google Business Profile', 'Start email collection'],
      industry_tactics: [`${industry}-specific strategies`, 'Competitor analysis', 'Market positioning']
    }
  });

  const getComplianceFallback = (industry) => ({
    licensing_requirements: {
      business_info: { industry },
      federal_licenses: [
        { name: 'EIN (Employer Identification Number)', agency: 'IRS', required: true },
        { name: 'DUNS Number', agency: 'Dun & Bradstreet', required: false }
      ],
      state_licenses: [
        { name: 'State Business Registration', required: true },
        { name: 'Sales Tax Permit', required: true },
        { name: 'Professional License', required: true }
      ],
      local_licenses: [
        { name: 'City Business License', required: true },
        { name: 'Zoning Permit', required: true },
        { name: 'Health Permit', required: industry.includes('Food') || industry.includes('Restaurant') }
      ],
      industry_licenses: [
        { name: `${industry} Specific Certification`, required: true },
        { name: 'Professional Association Membership', required: false }
      ]
    },
    companeze_services: [
      { service: 'Entity Formation', description: 'LLC, S-Corp, or C-Corp setup' },
      { service: 'License Research', description: 'Complete licensing requirements analysis' },
      { service: 'Document Preparation', description: 'All required forms and filings' },
      { service: 'Compliance Monitoring', description: 'Ongoing compliance tracking' },
      { service: 'Registered Agent', description: 'Official registered agent services' }
    ]
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-blue-900 text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-white hover:bg-blue-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Bot className="w-8 h-8 text-amber-400" />
              <div>
                <h1 className="text-xl font-bold">Advizzu Services</h1>
                <p className="text-blue-200 text-sm">A La Carte Business Solutions</p>
              </div>
            </div>
          </div>
          <a href="tel:5104224451" className="flex items-center gap-2 text-blue-200 hover:text-white">
            <Phone className="w-4 h-4" />
            (510) 422-4451
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border shadow-sm p-1 w-full grid grid-cols-4">
            <TabsTrigger value="services" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Sparkles className="w-4 h-4 mr-2" />
              Services
            </TabsTrigger>
            <TabsTrigger value="marketing" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Megaphone className="w-4 h-4 mr-2" />
              Marketing
            </TabsTrigger>
            <TabsTrigger value="compliance" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Shield className="w-4 h-4 mr-2" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="advisory" className="data-[state=active]:bg-blue-900 data-[state=active]:text-white">
              <Headphones className="w-4 h-4 mr-2" />
              Advisory
            </TabsTrigger>
          </TabsList>

          {/* Services Tab - A La Carte Menu */}
          <TabsContent value="services">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Object.entries(SERVICE_PRICING).map(([key, service]) => {
                const Icon = service.icon;
                return (
                  <Card key={key} className="hover:shadow-lg transition-shadow border-2 hover:border-blue-900">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-blue-900" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{service.name}</CardTitle>
                          <CardDescription>{service.description}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className="text-2xl font-bold text-blue-900">${service.perMinute.toFixed(2)}</span>
                          <span className="text-gray-500 text-sm">/minute</span>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">Per Minute Billing</Badge>
                      </div>
                      <Button 
                        className="w-full bg-blue-900 hover:bg-blue-800"
                        onClick={() => {
                          setSelectedService(key);
                          setActiveTab('advisory');
                        }}
                      >
                        Start Session
                        <ChevronRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
              
              {/* Full Service Package */}
              <Card className="border-2 border-amber-500 bg-gradient-to-br from-amber-50 to-white">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">BLDR Pro Package</CardTitle>
                      <CardDescription>Complete business development</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <span className="text-2xl font-bold text-amber-600">$199</span>
                    <span className="text-gray-500 text-sm">/month</span>
                  </div>
                  <ul className="space-y-2 mb-4 text-sm">
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Unlimited advisory calls</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Full compliance support</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Marketing strategy</li>
                    <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" /> Document templates</li>
                  </ul>
                  <Button 
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => window.open(BLDR_URL, '_blank')}
                  >
                    Upgrade to Pro
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Marketing Planner Tab */}
          <TabsContent value="marketing">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Input Section */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-900" />
                    Marketing Planner
                  </CardTitle>
                  <CardDescription>
                    Get a customized marketing program for your industry
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Select value={marketingIndustry} onValueChange={setMarketingIndustry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Budget Range</Label>
                    <Select value={marketingBudget} onValueChange={setMarketingBudget}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUDGET_TIERS.map(tier => (
                          <SelectItem key={tier.id} value={tier.id}>
                            {tier.label} ({tier.range})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    className="w-full bg-blue-900 hover:bg-blue-800"
                    onClick={generateMarketingPlan}
                    disabled={isGeneratingMarketing || !marketingIndustry || !marketingBudget}
                  >
                    {isGeneratingMarketing ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                    ) : (
                      <><Megaphone className="w-4 h-4 mr-2" /> Generate Plan</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Marketing Plan Output */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Your Marketing Program</CardTitle>
                  <CardDescription>
                    {marketingPlan ? `Customized for ${marketingIndustry}` : 'Select industry and budget to generate your plan'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!marketingPlan ? (
                    <div className="text-center py-12 text-gray-400">
                      <Megaphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Your marketing program will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Primary Channels */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <Globe className="w-4 h-4" /> Primary Channels
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {marketingPlan.marketing_plan?.primary_channels?.map((channel, idx) => (
                            <div key={idx} className="p-3 bg-blue-50 rounded-lg">
                              <p className="font-medium text-blue-900">{channel.channel}</p>
                              <ul className="mt-2 space-y-1">
                                {channel.tactics?.map((tactic, i) => (
                                  <li key={i} className="text-sm text-gray-600 flex items-start gap-1">
                                    <ChevronRight className="w-3 h-3 mt-1 text-blue-500" />
                                    {tactic}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Content Strategy */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> Content Strategy
                        </h4>
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Content Types</p>
                              <ul className="mt-1 space-y-1">
                                {marketingPlan.marketing_plan?.content_strategy?.content_types?.map((type, idx) => (
                                  <li key={idx} className="text-sm text-gray-600">• {type}</li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-700">Topics to Cover</p>
                              <ul className="mt-1 space-y-1">
                                {marketingPlan.marketing_plan?.content_strategy?.topics?.map((topic, idx) => (
                                  <li key={idx} className="text-sm text-gray-600">• {topic}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Plan */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <Calendar className="w-4 h-4" /> Monthly Action Plan
                        </h4>
                        <div className="space-y-2">
                          {marketingPlan.marketing_plan?.monthly_action_plan?.map((month, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 bg-white border rounded-lg">
                              <div className="w-10 h-10 rounded-full bg-blue-900 text-white flex items-center justify-center font-bold">
                                {month.month}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{month.focus}</p>
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {month.tasks?.map((task, i) => (
                                    <Badge key={i} variant="outline" className="text-xs">{task}</Badge>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Quick Wins */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                          <Sparkles className="w-4 h-4" /> Quick Wins
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {marketingPlan.marketing_plan?.quick_wins?.map((win, idx) => (
                            <Badge key={idx} className="bg-green-100 text-green-700">{win}</Badge>
                          ))}
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <p className="font-semibold text-amber-800 mb-2">Ready to Execute This Plan?</p>
                        <p className="text-sm text-amber-700 mb-3">
                          Our BLDR Pro package includes full marketing implementation support.
                        </p>
                        <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                          Upgrade to BLDR Pro - $199/mo
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Compliance Tab */}
          <TabsContent value="compliance">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Input Section */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-900" />
                    Compliance Checker
                  </CardTitle>
                  <CardDescription>
                    Discover licensing requirements and services for your industry
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Select value={complianceIndustry} onValueChange={setComplianceIndustry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {INDUSTRIES.map(ind => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button 
                    className="w-full bg-blue-900 hover:bg-blue-800"
                    onClick={generateComplianceData}
                    disabled={isGeneratingCompliance || !complianceIndustry}
                  >
                    {isGeneratingCompliance ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Shield className="w-4 h-4 mr-2" /> Check Requirements</>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Compliance Output */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Compliance Requirements</CardTitle>
                  <CardDescription>
                    {complianceData ? `Requirements for ${complianceIndustry}` : 'Select an industry to see requirements'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!complianceData ? (
                    <div className="text-center py-12 text-gray-400">
                      <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Compliance requirements will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Federal Licenses */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3">Federal Requirements</h4>
                        <div className="space-y-2">
                          {complianceData.licensing_requirements?.federal_licenses?.map((license, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                {license.required ? (
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-gray-400" />
                                )}
                                <span className="font-medium">{license.name}</span>
                              </div>
                              <Badge className={license.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}>
                                {license.required ? 'Required' : 'Optional'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* State Licenses */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3">State Requirements</h4>
                        <div className="space-y-2">
                          {complianceData.licensing_requirements?.state_licenses?.map((license, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{license.name}</span>
                              <Badge className={license.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}>
                                {license.required ? 'Required' : 'Optional'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Local Licenses */}
                      <div>
                        <h4 className="font-semibold text-blue-900 mb-3">Local Requirements</h4>
                        <div className="space-y-2">
                          {complianceData.licensing_requirements?.local_licenses?.map((license, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <span className="font-medium">{license.name}</span>
                              <Badge className={license.required ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}>
                                {license.required ? 'Required' : 'Conditional'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Companeze Services */}
                      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-blue-900 mb-3">Companeze Services for {complianceIndustry}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {complianceData.companeze_services?.map((service, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded">
                              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5" />
                              <div>
                                <p className="font-medium text-sm">{service.service}</p>
                                <p className="text-xs text-gray-500">{service.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button className="w-full mt-4 bg-blue-900 hover:bg-blue-800">
                          Get Compliance Support
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Advisory Tab - Per Minute Billing */}
          <TabsContent value="advisory">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Setup Section */}
              <div className="space-y-6">
                {/* Identity Verification */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5 text-blue-900" />
                      1. Identity Setup
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isIdentityVerified ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Verified: {clientName}</span>
                      </div>
                    ) : (
                      <>
                        <Input 
                          placeholder="Full Name" 
                          value={clientName} 
                          onChange={(e) => setClientName(e.target.value)}
                        />
                        <Input 
                          placeholder="Email Address" 
                          type="email"
                          value={clientEmail} 
                          onChange={(e) => setClientEmail(e.target.value)}
                        />
                        <Input 
                          placeholder="Company (Optional)" 
                          value={clientCompany} 
                          onChange={(e) => setClientCompany(e.target.value)}
                        />
                        <Button 
                          className="w-full"
                          onClick={setupIdentity}
                          disabled={!clientName || !clientEmail}
                        >
                          Verify Identity
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Payment Setup */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <CreditCard className="w-5 h-5 text-blue-900" />
                      2. Payment Method
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {isPaymentSetup ? (
                      <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700">
                        <CheckCircle2 className="w-5 h-5" />
                        <span>Card ending in ****{cardNumber.slice(-4)}</span>
                      </div>
                    ) : (
                      <>
                        <Input 
                          placeholder="Card Number" 
                          value={cardNumber} 
                          onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input 
                            placeholder="MM/YY" 
                            value={cardExpiry} 
                            onChange={(e) => setCardExpiry(e.target.value)}
                          />
                          <Input 
                            placeholder="CVC" 
                            value={cardCvc} 
                            onChange={(e) => setCardCvc(e.target.value.slice(0, 4))}
                          />
                        </div>
                        <Input 
                          placeholder="Name on Card" 
                          value={cardName} 
                          onChange={(e) => setCardName(e.target.value)}
                        />
                        <Button 
                          className="w-full"
                          onClick={setupPayment}
                          disabled={!cardNumber || !cardExpiry || !cardCvc || !cardName}
                        >
                          Save Payment Method
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Service Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Sparkles className="w-5 h-5 text-blue-900" />
                      3. Select Service
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={selectedService} onValueChange={setSelectedService}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose service type" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SERVICE_PRICING).map(([key, service]) => (
                          <SelectItem key={key} value={key}>
                            {service.name} - ${service.perMinute.toFixed(2)}/min
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedService && (
                      <p className="mt-2 text-sm text-gray-500">
                        {SERVICE_PRICING[selectedService]?.description}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Call Interface */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Headphones className="w-5 h-5 text-blue-900" />
                    Advisory Call Session
                  </CardTitle>
                  <CardDescription>
                    We will call you at the number provided. Per-minute billing applies.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Phone Number Input */}
                  <div className="mb-6">
                    <Label className="mb-2 block">Your Phone Number (We Call You)</Label>
                    <div className="flex gap-2">
                      <Input 
                        placeholder="(555) 555-5555" 
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="flex-1"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      * We do not accept incoming calls for free advisory. Outbound calls only.
                    </p>
                  </div>

                  {/* Call Status Display */}
                  <div className={`p-8 rounded-xl text-center mb-6 ${isCallActive ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50'}`}>
                    {isCallActive ? (
                      <>
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center animate-pulse">
                          <Phone className="w-10 h-10 text-white" />
                        </div>
                        <p className="text-green-700 font-semibold mb-2">Call in Progress</p>
                        <p className="text-4xl font-mono font-bold text-green-900 mb-2">{formatTime(callDuration)}</p>
                        <p className="text-lg text-green-700">
                          Current Charge: <span className="font-bold">${callCost.toFixed(2)}</span>
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                          <Phone className="w-10 h-10 text-gray-400" />
                        </div>
                        <p className="text-gray-500 mb-2">Ready to Start</p>
                        <p className="text-4xl font-mono font-bold text-gray-300 mb-2">00:00</p>
                        <p className="text-gray-400">
                          Rate: ${SERVICE_PRICING[selectedService]?.perMinute.toFixed(2) || '0.00'}/minute
                        </p>
                      </>
                    )}
                  </div>

                  {/* Call Controls */}
                  <div className="flex justify-center gap-4">
                    {!isCallActive ? (
                      <Button 
                        size="lg"
                        className="bg-green-600 hover:bg-green-700 text-white px-8"
                        onClick={startCall}
                        disabled={!isPaymentSetup || !isIdentityVerified || !selectedService || !phoneNumber}
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Start Call
                      </Button>
                    ) : (
                      <Button 
                        size="lg"
                        className="bg-red-600 hover:bg-red-700 text-white px-8"
                        onClick={endCall}
                      >
                        <Pause className="w-5 h-5 mr-2" />
                        End Call
                      </Button>
                    )}
                  </div>

                  {/* Requirements Checklist */}
                  {(!isPaymentSetup || !isIdentityVerified || !selectedService) && (
                    <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="font-semibold text-amber-800 mb-2">Before Starting:</p>
                      <ul className="space-y-1 text-sm text-amber-700">
                        <li className="flex items-center gap-2">
                          {isIdentityVerified ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4" />}
                          Complete identity verification
                        </li>
                        <li className="flex items-center gap-2">
                          {isPaymentSetup ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4" />}
                          Add payment method
                        </li>
                        <li className="flex items-center gap-2">
                          {selectedService ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4" />}
                          Select a service type
                        </li>
                        <li className="flex items-center gap-2">
                          {phoneNumber ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4" />}
                          Enter your phone number
                        </li>
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdvizzuServices;
